import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogOut, ShieldCheck, Sun, Moon } from "lucide-react";
import { api } from "../lib/api";

// Darken a hex colour by `amount` (0..1). Used to derive a hover state from the
// platform-configured brand primary so :hover stays readable across any theme.
function _darken(hex, amount = 0.15) {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.max(0, ((n >> 16) & 255) - Math.round(255 * amount));
  const g = Math.max(0, ((n >> 8) & 255) - Math.round(255 * amount));
  const b = Math.max(0, (n & 255) - Math.round(255 * amount));
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}

export default function Nav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAuthed = user && user !== false;
  const role = user?.role;
  const [brand, setBrand] = useState({ site_name: "Ocean Ash Ltd", logo_url: "", primary_color: "#D94833" });

  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("theme") === "dark");
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);
  useEffect(() => { api.get("/platform/branding").then((r) => setBrand(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    // Dynamic favicon + tab title + global brand colour from platform branding
    if (brand.site_name) {
      document.title = `${brand.site_name} Careers`;
    }
    let link = document.getElementById("app-favicon");
    if (!link) {
      link = document.createElement("link");
      link.id = "app-favicon";
      link.rel = "icon";
      document.head.appendChild(link);
    }
    if (brand.logo_url) {
      link.href = brand.logo_url;
    }
    // Apply platform-wide brand colours as CSS variables on :root.
    // Components that read var(--brand-primary) (.btn-terra, focus outlines,
    // selection, custom inline styles) will repaint instantly.
    const primary = brand.primary_color || "#D94833";
    document.documentElement.style.setProperty("--brand-primary", primary);
    document.documentElement.style.setProperty("--brand-primary-hover", _darken(primary, 0.15));
    document.documentElement.style.setProperty("--brand-accent", brand.accent_color || primary);
  }, [brand.logo_url, brand.site_name, brand.primary_color, brand.accent_color]);

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#FDFCF8]/85 dark:bg-[#121212]/90 border-b border-[#E2E0D8] dark:border-[#333]"
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.site_name} className="w-8 h-8 object-contain" data-testid="platform-logo" />
          ) : (
            <div className="w-8 h-8 flex items-center justify-center" style={{ background: brand.primary_color || "#0F0F0F" }}>
              <span className="text-[#FDFCF8] font-display font-black text-xs tracking-wider">{(brand.site_name || "OA").slice(0, 2).toUpperCase()}</span>
            </div>
          )}
          <div className="leading-none">
            <div className="font-display font-black text-base tracking-tight">{brand.site_name}</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C5C5C] dark:text-[#999]">Careers</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm font-medium dark:text-[#FDFCF8]">
          <Link to="/jobs" data-testid="nav-jobs" className="hover:text-[var(--brand-primary)] transition">Open Roles</Link>
          <Link to="/pricing" data-testid="nav-pricing" className="hover:text-[var(--brand-primary)] transition">Pricing</Link>
          <a href="https://contact.oceanash.org" target="_blank" rel="noopener noreferrer" data-testid="nav-contact" className="hover:text-[var(--brand-primary)] transition">Support</a>
          {isAuthed && (role === "recruiter" || role === "owner") && (
            <>
              <Link to="/recruiter" data-testid="nav-recruiter" className="hover:text-[var(--brand-primary)] transition">Recruiter</Link>
              <Link to="/hr" data-testid="nav-hr" className="hover:text-[var(--brand-primary)] transition inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary)] inline-block" /> HR</Link>
            </>
          )}
          {isAuthed && (role === "recruiter" || role === "owner") && (
            <Link to="/pipeline" data-testid="nav-pipeline" className="hover:text-[var(--brand-primary)] transition">Pipeline</Link>
          )}
          {isAuthed && role === "candidate" && (
            <Link to="/candidate" data-testid="nav-candidate" className="hover:text-[var(--brand-primary)] transition">My Applications</Link>
          )}
          {isAuthed && (role === "owner" || role === "moderator") && (
            <Link to="/admin" data-testid="nav-admin" className="hover:text-[var(--brand-primary)] transition inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> {role === "owner" ? "Owner Console" : "Moderator Console"}
            </Link>
          )}
          {isAuthed && (
            <Link to="/settings" data-testid="nav-settings" className="hover:text-[var(--brand-primary)] transition">Settings</Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-1.5 border border-[#E2E0D8] hover:bg-[#0F0F0F]/5 dark:hover:bg-[#FDFCF8]/10 text-[#0F0F0F] dark:text-[#FDFCF8] rounded-sm transition flex items-center justify-center"
            title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            data-testid="dark-mode-toggle"
          >
            {darkMode ? <Sun className="w-4 h-4 text-amber-500" /> : <Moon className="w-4 h-4 text-indigo-900" />}
          </button>
          {!isAuthed ? (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-semibold hover:text-[var(--brand-primary)]">Sign in</Link>
              <Link to="/register" data-testid="nav-register" className="btn-terra text-sm">Create account</Link>
            </>
          ) : (
            <>
              <div className="hidden sm:flex flex-col items-end mr-1">
                <span className={`text-[10px] uppercase tracking-widest leading-none ${role === "owner" ? "text-[var(--brand-primary)] font-bold" : role === "moderator" ? "text-[#0F0F0F] font-bold" : "text-[#5C5C5C]"}`}>
                  {role === "owner" ? "★ Owner" : role === "moderator" ? "● Moderator" : role}
                </span>
                <span className="text-sm font-semibold leading-tight">{user.name || user.email}</span>
              </div>
              <button
                data-testid="logout-button"
                onClick={async () => { await logout(); navigate("/"); }}
                className="btn-ghost text-sm px-3 py-2"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
